import { forwardRef, useId } from 'react'
import clsx from 'clsx'
import { AlertCircle, ChevronDown, Eye, EyeOff, Search } from 'lucide-react'
import { useState } from 'react'

/**
 * Form fields.
 *
 * Every input has a visible label, floated onto the top border once the field
 * has a value or focus. Placeholder-as-label — where the label is real
 * placeholder text and disappears the moment someone types — is still the
 * single most common form mistake this avoids: here the label is a real
 * <label> the whole time, sitting centred in the field only while it is both
 * empty and unfocused, and animating up to the border rather than vanishing.
 * A screen reader always has the real label; this is a visual affordance on
 * top of it, not a replacement for it.
 *
 * Built on the `peer` + `:placeholder-shown` CSS trick: every input below
 * carries `placeholder=" "` (a single space, never actually shown) purely so
 * `:placeholder-shown` is true exactly when the field is empty, which is what
 * the label position is keyed on.
 *
 * Errors render directly beneath the field they belong to, are announced via
 * role="alert", and mark the input with aria-invalid — so the message reaches
 * someone who cannot see the red border.
 */

/**
 * The floating label itself.
 *
 * Sits ON the border (centred on the field's top edge), same as HRM-3.0's
 * own OutlinedInput — matched deliberately rather than reinvented, since
 * that pattern is the one already proven across this client's other product.
 * A small opaque background patch under the label is what makes it read as
 * sitting "on" the border rather than being crossed by it; the patch colour
 * has to equal the field's OWN rendered background in every state (default,
 * focused, error) or it shows up as a mismatched box instead of a seamless
 * cut in the line — which is what removing the patch entirely was trying,
 * wrongly, to fix. The field's background never actually changes, so one
 * colour covers every state.
 */
function FloatingLabel({ htmlFor, label, required }) {
  return (
    <label
      htmlFor={htmlFor}
      className={clsx(
        'pointer-events-none absolute left-2.5 top-0 -translate-y-1/2 rounded px-1.5',
        'bg-[var(--color-surface)] text-xs text-[var(--color-ink-500)]',
        'transition-all duration-150',
        'peer-placeholder-shown:top-1/2 peer-placeholder-shown:bg-transparent',
        'peer-placeholder-shown:text-[0.9375rem] peer-placeholder-shown:text-[var(--color-ink-400)]',
        'peer-focus:top-0 peer-focus:bg-[var(--color-surface)] peer-focus:text-xs peer-focus:text-[var(--color-accent-text)]',
      )}
    >
      {label}
      {required && (
        <span className="ml-0.5 text-[var(--color-danger)]" aria-hidden="true">*</span>
      )}
      {required && <span className="sr-only"> (required)</span>}
    </label>
  )
}

/**
 * A conventional label above the field, kept for controls where the floating
 * pattern does not apply — a <select> always renders text (its selected
 * option, or the placeholder option), so there is no "vanishes when you
 * start typing" problem for it to solve, and forcing the floating mechanic
 * onto it would need extra machinery for no real benefit.
 */
function StaticLabel({ htmlFor, label, required }) {
  return (
    <label
      htmlFor={htmlFor}
      className="mb-1.5 block text-sm font-medium text-[var(--color-ink-700)]"
    >
      {label}
      {required && (
        <span className="ml-0.5 text-[var(--color-danger)]" aria-hidden="true">*</span>
      )}
      {required && <span className="sr-only"> (required)</span>}
    </label>
  )
}

function FieldShell({ id, hint, error, children, className }) {
  const describedBy = [hint && `${id}-hint`, error && `${id}-error`].filter(Boolean).join(' ')

  return (
    <div className={clsx('w-full', className)}>
      {/* Helper text sits ABOVE the input, where it is read before the person
          starts typing rather than after they have already got it wrong. A
          floated label leaves no room for it inside the field itself, the
          way a hint sentence would if it tried to live in the same space. */}
      {hint && !error && (
        <p id={`${id}-hint`} className="mb-1.5 text-xs text-[var(--color-ink-500)]">
          {hint}
        </p>
      )}

      {children(describedBy || undefined)}

      {error && (
        <p
          id={`${id}-error`}
          role="alert"
          className="mt-1.5 flex items-start gap-1 text-xs text-[var(--color-danger)]"
        >
          <AlertCircle size={13} className="mt-0.5 shrink-0" aria-hidden="true" />
          <span>{error}</span>
        </p>
      )}
    </div>
  )
}

// focus:outline-none/focus-visible:outline-none drop the global focus ring
// (index.css's :focus-visible) specifically on these fields — not elsewhere
// in the app — because the field's own focus border is already an equally
// visible focus indicator, and stacking the browser's ring on top of it is
// what produced a double outline rather than one clear one.
const baseInput =
  'peer w-full rounded-md border bg-[var(--color-surface)] px-3 text-[0.9375rem] ' +
  'text-[var(--color-ink-900)] placeholder:text-transparent ' +
  'outline-none focus-visible:outline-none ' +
  'transition-colors duration-150 ' +
  'disabled:cursor-not-allowed disabled:bg-[var(--color-surface-sunken)] disabled:opacity-60'

function borderFor(error) {
  // The focus ring shadow is the field's entire visible focus indicator now
  // that the browser's own outline is suppressed above — a border colour
  // change alone is too subtle on a 1px line, so a soft glow reinforces it
  // without reproducing the double-ring the browser outline used to cause.
  return error
    ? 'border-[var(--color-danger)] focus:ring-2 focus:ring-[var(--color-danger)]/25'
    : 'border-[var(--color-line-strong)] hover:border-[var(--color-ink-400)] ' +
      'focus:border-[var(--color-accent)] focus:ring-2 focus:ring-[var(--color-accent)]/25'
}

export const TextField = forwardRef(function TextField(
  { label, hint, error, required, className, inputClassName, staticLabel, ...props },
  ref,
) {
  const generatedId = useId()
  const id = props.id ?? generatedId

  // Static rather than floating for two real reasons, either of which is
  // enough on its own: a date/time/number input always shows browser chrome
  // (the "dd-mm-yyyy" placeholder, or a number input's up/down spinner) even
  // when empty, so it never reads as truly empty the way a text input does
  // and the floating mechanic has nothing reliable to key off — a number
  // field's own spinner and its native focus box also do not fully respect
  // the outline-none/ring treatment baseInput applies for the floating
  // variant, which showed up as a stray red focus box the field-level ring
  // didn't fully cover. A field placed in a row of SelectFields (which are
  // always static — see StaticLabel above) also needs the same label
  // position to keep that row's labels aligned, rather than one field
  // sitting lower than its neighbours.
  const isChromeHeavy = ['date', 'time', 'datetime-local', 'number'].includes(props.type)
  const useStatic = staticLabel ?? isChromeHeavy

  if (useStatic) {
    return (
      <FieldShell id={id} hint={hint} error={error} className={className}>
        {(describedBy) => (
          <div>
            <StaticLabel htmlFor={id} label={label} required={required} />
            <input
              ref={ref}
              id={id}
              aria-invalid={error ? 'true' : undefined}
              aria-describedby={describedBy}
              className={clsx(baseInput, borderFor(error), 'h-11', inputClassName)}
              {...props}
            />
          </div>
        )}
      </FieldShell>
    )
  }

  return (
    <FieldShell id={id} hint={hint} error={error} className={className}>
      {(describedBy) => (
        <div className="relative">
          <input
            ref={ref}
            id={id}
            placeholder=" "
            aria-invalid={error ? 'true' : undefined}
            aria-describedby={describedBy}
            // 44px tall: the touch-target floor, and 16px text so iOS does not
            // zoom the page when the field is focused.
            className={clsx(baseInput, borderFor(error), 'h-11', inputClassName)}
            {...props}
          />
          <FloatingLabel htmlFor={id} label={label} required={required} />
        </div>
      )}
    </FieldShell>
  )
})

export const TextArea = forwardRef(function TextArea(
  { label, hint, error, required, rows = 5, maxLength, value, className, ...props },
  ref,
) {
  const generatedId = useId()
  const id = props.id ?? generatedId

  return (
    <FieldShell id={id} hint={hint} error={error} className={className}>
      {(describedBy) => (
        <div className="relative">
          <textarea
            ref={ref}
            id={id}
            rows={rows}
            maxLength={maxLength}
            value={value}
            placeholder=" "
            aria-invalid={error ? 'true' : undefined}
            aria-describedby={describedBy}
            className={clsx(baseInput, borderFor(error), 'pt-5 pb-2.5 leading-relaxed resize-y min-h-[7rem]')}
            {...props}
          />

          {/* Anchored to the top-left rather than vertically centred — a
              textarea is tall, and a label centred in it would sit in the
              middle of empty space rather than where writing starts. It still
              floats onto the border, same as a single-line field; the
              default classes here ARE the floated position, because that has
              to be correct whenever the field has a value, focused or not —
              only the empty-and-unfocused case (peer-placeholder-shown)
              overrides back to sitting inside the box. */}
          <label
            htmlFor={id}
            className={clsx(
              'pointer-events-none absolute left-2.5 top-0 -translate-y-1/2 rounded px-1.5',
              'bg-[var(--color-surface)] text-xs text-[var(--color-ink-500)]',
              'transition-all duration-150',
              'peer-placeholder-shown:top-3 peer-placeholder-shown:translate-y-0 peer-placeholder-shown:bg-transparent',
              'peer-placeholder-shown:text-[0.9375rem] peer-placeholder-shown:text-[var(--color-ink-400)]',
              'peer-focus:top-0 peer-focus:-translate-y-1/2 peer-focus:bg-[var(--color-surface)] peer-focus:text-xs peer-focus:text-[var(--color-accent-text)]',
            )}
          >
            {label}
            {required && (
              <span className="ml-0.5 text-[var(--color-danger)]" aria-hidden="true">*</span>
            )}
            {required && <span className="sr-only"> (required)</span>}
          </label>

          {/* Only shown once the limit is in sight. A counter from character
              zero reads as pressure to be brief, which is the opposite of what
              someone describing a grievance should feel. */}
          {maxLength && value != null && value.length > maxLength * 0.7 && (
            <span className="pointer-events-none absolute bottom-2 right-3 text-xs tnum text-[var(--color-ink-400)]">
              {value.length} / {maxLength}
            </span>
          )}
        </div>
      )}
    </FieldShell>
  )
})

export const SelectField = forwardRef(function SelectField(
  { label, hint, error, required, options = [], placeholder, className, children, ...props },
  ref,
) {
  const generatedId = useId()
  const id = props.id ?? generatedId

  return (
    <FieldShell id={id} hint={hint} error={error} className={className}>
      {(describedBy) => (
        <div>
          <StaticLabel htmlFor={id} label={label} required={required} />

          <div className="relative">
            <select
              ref={ref}
              id={id}
              aria-invalid={error ? 'true' : undefined}
              aria-describedby={describedBy}
              className={clsx(
                baseInput,
                borderFor(error),
                'h-11 cursor-pointer appearance-none pr-9',
              )}
              {...props}
            >
              {placeholder && <option value="">{placeholder}</option>}

              {children ??
                options.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
            </select>

            <ChevronDown
              size={16}
              aria-hidden="true"
              className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-[var(--color-ink-500)]"
            />
          </div>
        </div>
      )}
    </FieldShell>
  )
})

export const PasswordField = forwardRef(function PasswordField(
  { label, hint, error, required, className, ...props },
  ref,
) {
  const generatedId = useId()
  const id = props.id ?? generatedId
  const [visible, setVisible] = useState(false)

  return (
    <FieldShell id={id} hint={hint} error={error} className={className}>
      {(describedBy) => (
        <div className="relative">
          <input
            ref={ref}
            id={id}
            type={visible ? 'text' : 'password'}
            placeholder=" "
            aria-invalid={error ? 'true' : undefined}
            aria-describedby={describedBy}
            className={clsx(baseInput, borderFor(error), 'h-11 pr-11')}
            {...props}
          />
          <FloatingLabel htmlFor={id} label={label} required={required} />

          {/* A reveal toggle measurably reduces sign-in failures, especially on
              phone keyboards. The button is outside the tab order of the form's
              happy path but still reachable. */}
          <button
            type="button"
            onClick={() => setVisible((v) => !v)}
            aria-label={visible ? 'Hide password' : 'Show password'}
            className="absolute right-1 top-1/2 flex h-9 w-9 -translate-y-1/2 cursor-pointer items-center justify-center rounded text-[var(--color-ink-500)] hover:text-[var(--color-ink-900)]"
          >
            {visible ? <EyeOff size={16} aria-hidden="true" /> : <Eye size={16} aria-hidden="true" />}
          </button>
        </div>
      )}
    </FieldShell>
  )
})

export function CheckboxField({ label, hint, checked, onChange, disabled, className, ...props }) {
  const id = useId()

  return (
    <div className={clsx('flex items-start gap-2.5', className)}>
      <input
        id={id}
        type="checkbox"
        checked={checked}
        onChange={onChange}
        disabled={disabled}
        aria-describedby={hint ? `${id}-hint` : undefined}
        // appearance-none + a hand-drawn box: accent-color alone only styles
        // the CHECKED fill, so the unchecked state falls back to the
        // browser's own native rendering — on at least Chrome/Windows that
        // renders as a solid black square rather than a neutral outline,
        // which is what showed up here. The checkmark itself is drawn by the
        // .field-checkbox rule in index.css (a plain CSS class rather than a
        // Tailwind arbitrary-value background-image, which would need the
        // inline SVG's spaces and quotes awkwardly re-encoded to survive as
        // a class string).
        className={clsx(
          'field-checkbox mt-0.5 h-[18px] w-[18px] shrink-0 cursor-pointer appearance-none rounded border',
          'border-[var(--color-line-strong)] bg-[var(--color-surface)]',
          'checked:border-[var(--color-accent)] checked:bg-[var(--color-accent)]',
          'disabled:cursor-not-allowed disabled:opacity-60',
        )}
        {...props}
      />

      <div className="min-w-0">
        <label
          htmlFor={id}
          className={clsx(
            'block text-sm text-[var(--color-ink-900)]',
            disabled ? 'cursor-not-allowed opacity-60' : 'cursor-pointer',
          )}
        >
          {label}
        </label>

        {hint && (
          <p id={`${id}-hint`} className="mt-0.5 text-xs text-[var(--color-ink-500)]">
            {hint}
          </p>
        )}
      </div>
    </div>
  )
}

/**
 * Radio group as selectable cards.
 *
 * Used where the choice carries consequences the person needs to understand
 * before making it — anonymous versus named submission, most of all. A bare
 * radio label has nowhere to explain the trade-off.
 */
export function RadioCards({ label, hint, options, value, onChange, required, error, name, className }) {
  const groupName = useId()

  return (
    <fieldset className={clsx('w-full', className)}>
      <legend className="mb-1.5 text-sm font-medium text-[var(--color-ink-700)]">
        {label}
        {required && <span className="ml-0.5 text-[var(--color-danger)]" aria-hidden="true">*</span>}
      </legend>

      {hint && <p className="mb-2 text-xs text-[var(--color-ink-500)]">{hint}</p>}

      <div className="grid gap-2 sm:grid-cols-2">
        {options.map((option) => {
          const selected = value === option.value

          return (
            <label
              key={option.value}
              className={clsx(
                'flex cursor-pointer items-start gap-2.5 rounded-lg border p-3 transition-colors duration-150',
                selected
                  ? 'border-[var(--color-accent)] bg-[var(--color-accent-soft)]'
                  : 'border-[var(--color-line-strong)] hover:border-[var(--color-ink-400)] hover:bg-[var(--color-surface-sunken)]',
              )}
            >
              <input
                type="radio"
                name={name ?? groupName}
                value={option.value}
                checked={selected}
                onChange={() => onChange(option.value)}
                // Same reasoning as CheckboxField: appearance-none plus an
                // explicit unchecked style, rather than relying on
                // accent-color to also fix up the browser's native unchecked
                // rendering, which it does not touch.
                className={clsx(
                  'field-radio mt-0.5 h-4 w-4 shrink-0 cursor-pointer appearance-none rounded-full border',
                  'border-[var(--color-line-strong)] bg-[var(--color-surface)]',
                  'checked:border-[var(--color-accent)] checked:bg-[var(--color-accent)]',
                )}
              />

              <div className="min-w-0">
                <span className="block text-sm font-medium text-[var(--color-ink-900)]">
                  {option.label}
                </span>
                {option.description && (
                  <span className="mt-0.5 block text-xs leading-relaxed text-[var(--color-ink-500)]">
                    {option.description}
                  </span>
                )}
              </div>
            </label>
          )
        })}
      </div>

      {error && (
        <p role="alert" className="mt-1.5 text-xs text-[var(--color-danger)]">{error}</p>
      )}
    </fieldset>
  )
}

/**
 * A search box for a filter toolbar — deliberately not a floating-label
 * field. It sits in a row of quick filters and tabs, not a form, and a label
 * that floats onto the border does not fit that context; a leading icon and
 * a plain placeholder read as "search" immediately, the way it does in the
 * rest of the industry.
 *
 * Extracted from GrievanceQueue.jsx so the search box looks and behaves
 * identically everywhere it appears — same icon position, same height, same
 * border colours — rather than each list screen re-implementing it slightly
 * differently and drifting apart over time.
 */
export const SearchField = forwardRef(function SearchField(
  { className, wrapperClassName, ...props },
  ref,
) {
  return (
    <div className={clsx('relative min-w-0', wrapperClassName)}>
      <Search
        size={15}
        aria-hidden="true"
        className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-[var(--color-ink-400)]"
      />
      <input
        ref={ref}
        type="search"
        className={clsx(
          'h-10 w-full rounded-md border border-[var(--color-line-strong)] bg-[var(--color-surface)]',
          'pl-9 pr-3 text-sm text-[var(--color-ink-900)] placeholder:text-[var(--color-ink-400)]',
          'transition-colors duration-150 focus:border-[var(--color-accent)]',
          className,
        )}
        {...props}
      />
    </div>
  )
})
